import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Button,
  Text,
  Container,
  Heading,
  useColorModeValue,
  Icon,
  HStack,
  useDisclosure,
  Badge,
  SimpleGrid,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { FaTrophy, FaDice, FaList, FaBrain, FaChartLine } from 'react-icons/fa';
import { useGame, DIFFICULTY_LEVELS } from '../context/GameContext';
import { categories } from '../services/imageService';
import LeaderboardModal from './LeaderboardModal';
import { getStoredGames } from '../services/leaderboardService';
import AchievementsModal from './AchievementsModal';

const HomePage = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const gradientBg = useColorModeValue(
    'linear(to-r, blue.400, purple.500)',
    'linear(to-r, blue.200, purple.300)'
  );
  const { difficulty, setDifficulty } = useGame();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const games = getStoredGames();
  const { 
    isOpen: isAchievementsOpen, 
    onOpen: onAchievementsOpen, 
    onClose: onAchievementsClose 
  } = useDisclosure();

  // Calculate stats
  const totalGames = games.length;
  const highScore = Math.max(...games.map(g => g.score || 0), 0);
  const averageScore = totalGames ? Math.round(games.reduce((sum, g) => sum + (g.score || 0), 0) / totalGames) : 0;

  const handleRandomPlay = () => {
    const categoryIds = Object.keys(categories);
    const numCategories = Math.floor(Math.random() * 3) + 1;
    const selectedCategories = [];
    
    while (selectedCategories.length < numCategories) {
      const randomId = categoryIds[Math.floor(Math.random() * categoryIds.length)];
      if (!selectedCategories.includes(randomId)) {
        selectedCategories.push(randomId);
      }
    }

    navigate(`/play?categories=${selectedCategories.join(',')}`);
  };

  return (
    <Container maxW="container.lg" centerContent py={8}>
      <VStack spacing={8} width="100%">
        {/* Hero Section */}
        <Box textAlign="center" py={10} px={6} width="100%">
          <Heading
            as="h1"
            size="2xl"
            bgGradient={gradientBg}
            backgroundClip="text"
            mb={4}
            fontWeight="extrabold"
          >
            Blur to Clear
          </Heading>
          <Text fontSize="xl" color="gray.600" maxW="600px" mx="auto">
            Test your perception! Guess the image as it becomes clearer in this engaging visual challenge.
          </Text>
        </Box>

        {/* Stats Section */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} width="100%">
          <Stat
            bg={useColorModeValue('yellow.50', 'yellow.900')}
            p={4}
            borderRadius="lg"
            boxShadow="md"
            textAlign="center"
          >
            <StatLabel fontSize="lg">High Score</StatLabel>
            <StatNumber fontSize="3xl" color="yellow.500">{highScore}</StatNumber>
            <StatHelpText><Icon as={FaTrophy} /></StatHelpText>
          </Stat>
          <Stat
            bg={useColorModeValue('blue.50', 'blue.900')}
            p={4}
            borderRadius="lg"
            boxShadow="md"
            textAlign="center"
          >
            <StatLabel fontSize="lg">Games Played</StatLabel>
            <StatNumber fontSize="3xl" color="blue.500">{totalGames}</StatNumber>
            <StatHelpText><Icon as={FaChartLine} /></StatHelpText>
          </Stat>
          <Stat
            bg={useColorModeValue('green.50', 'green.900')}
            p={4}
            borderRadius="lg"
            boxShadow="md"
            textAlign="center"
          >
            <StatLabel fontSize="lg">Avg Score</StatLabel>
            <StatNumber fontSize="3xl" color="green.500">{averageScore}</StatNumber>
            <StatHelpText><Icon as={FaBrain} /></StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Difficulty Selection */}
        <Box
          w="100%"
          p={6}
          bg={bgColor}
          borderRadius="xl"
          boxShadow="xl"
        >
          <VStack spacing={6}>
            <Text fontSize="lg" fontWeight="bold" color="gray.600">
              Select Difficulty
            </Text>
            <HStack spacing={4} justify="center" wrap="wrap">
              {Object.values(DIFFICULTY_LEVELS).map((level) => (
                <Button
                  key={level.name}
                  size="lg"
                  colorScheme={level.color}
                  variant={difficulty === level ? 'solid' : 'outline'}
                  onClick={() => setDifficulty(level)}
                  minW="120px"
                  h="50px"
                >
                  {level.name}
                </Button>
              ))}
            </HStack>

            <Flex direction={{ base: 'column', md: 'row' }} gap={4} width="100%" maxW="600px" mt={4}>
              <Button
                size="lg"
                colorScheme="blue"
                width="100%"
                height="60px"
                onClick={handleRandomPlay}
                leftIcon={<Icon as={FaDice} boxSize={6} />}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                Random Play
              </Button>
              <Button
                size="lg"
                colorScheme="purple"
                width="100%"
                height="60px"
                onClick={() => navigate('/categories')}
                leftIcon={<Icon as={FaList} boxSize={6} />}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                Categories
              </Button>
              <Button
                size="lg"
                colorScheme="yellow"
                width="100%"
                height="60px"
                onClick={onAchievementsOpen}
                leftIcon={<Icon as={FaTrophy} boxSize={6} />}
                variant="outline"
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                Achievements
              </Button>
            </Flex>

            <Button
              size="lg"
              colorScheme="teal"
              width="100%"
              maxW="600px"
              height="60px"
              onClick={onOpen}
              leftIcon={<Icon as={FaTrophy} boxSize={6} />}
              variant="outline"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              Leaderboard
            </Button>
          </VStack>
        </Box>

        {/* Difficulty Info */}
        <Box
          p={6}
          borderRadius="lg"
          bg={useColorModeValue('blue.50', 'blue.900')}
          width="100%"
          maxW="600px"
        >
          <VStack spacing={3}>
            <Badge colorScheme={difficulty.color} p={2} fontSize="md">
              {difficulty.name} Mode Selected
            </Badge>
            <Text color={useColorModeValue('blue.600', 'blue.100')} textAlign="center">
              {difficulty === DIFFICULTY_LEVELS.EASY && "Perfect for beginners! Images clear up faster, giving you a better chance to guess correctly."}
              {difficulty === DIFFICULTY_LEVELS.MEDIUM && "The balanced challenge! Images clear gradually, testing your perception skills."}
              {difficulty === DIFFICULTY_LEVELS.HARD && "For the experts! Images stay blurrier longer, providing the ultimate challenge."}
            </Text>
          </VStack>
        </Box>
      </VStack>

      <LeaderboardModal isOpen={isOpen} onClose={onClose} />
      <AchievementsModal isOpen={isAchievementsOpen} onClose={onAchievementsClose} />
    </Container>
  );
};

export default HomePage; 